/**
 * Fixed Routes — Costs section (statistics drawer).
 *
 * Lets a planner set the network-default operating-cost rates (daily fixed cost per vehicle,
 * cost per km, hourly wage) + currency, shows the network cost rollup (daily/annual + a
 * fixed/distance/labour breakdown), and lists per-route costs with an inline per-route override
 * editor. The cost model lives in `services/fixedRoutes/fixedRouteCostCalculator.ts`.
 *
 * The rollup previews live from the local editor form so editing rates doesn't re-render the
 * heavy RouteBuilder on every keystroke; the committed settings persist to context on blur.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Divider,
  TextField,
  MenuItem,
  IconButton,
  Collapse,
  Tooltip,
  Chip,
  InputAdornment,
} from '@mui/material';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { useFixedRoutes } from '../../context/FixedRoutesContext';
import type { FixedRoute, CostRates } from '../../types/fixedRoutes';
import { logEvent } from '../../services/analytics';
import { formatCurrency, formatCurrencyCompact } from '../../utils/fixedRoutes/currencyFormatter';
import {
  computeNetworkCost,
  parseCostSettings,
  costSettingsToFormValues,
  parseNonNegativeNumber,
  type CostSettingsFormValues,
  type CostRateFieldKey,
} from '../../services/fixedRoutes/fixedRouteCostCalculator';

/** Common currency options for the rate editor. */
const CURRENCY_OPTIONS = ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'NZD', 'SGD', 'AED'];

const RATE_FIELDS: ReadonlyArray<{ key: CostRateFieldKey; labelKey: string; iconPath: string }> = [
  { key: 'dailyFixedCostPerVehicle', labelKey: 'cost.dailyFixedCostPerVehicle', iconPath: ICON_FALLBACKS.DirectionsBusOutlined },
  { key: 'costPerKm', labelKey: 'cost.costPerKm', iconPath: ICON_FALLBACKS.Straighten },
  { key: 'hourlyWage', labelKey: 'cost.hourlyWage', iconPath: ICON_FALLBACKS.AccessTimeOutlined },
];

export function FixedRoutesCostSection() {
  const { t } = useTranslation('fixedRoutes');
  const { routes, costSettings, setCostSettings, updateRoute } = useFixedRoutes();

  const [form, setForm] = useState<CostSettingsFormValues>(() => costSettingsToFormValues(costSettings));
  const [expandedRouteId, setExpandedRouteId] = useState<string | null>(null);

  // One analytics ping when the cost section is first rendered with routes present.
  useEffect(() => {
    logEvent({ category: 'fixed_routes', action: 'fixed_route_cost_viewed', route_count: routes.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsedForm = useMemo(() => parseCostSettings(form), [form]);
  // Preview the rollup from the live (valid) form; fall back to committed settings while invalid.
  const activeSettings = parsedForm.isValid && parsedForm.settings ? parsedForm.settings : costSettings;

  const network = useMemo(() => computeNetworkCost(routes, activeSettings), [routes, activeSettings]);
  const sortedRoutes = useMemo(
    () => [...network.perRoute].sort((a, b) => b.dailyCost - a.dailyCost),
    [network.perRoute],
  );
  const routeById = useMemo(() => new Map(routes.map((r) => [r.id, r])), [routes]);

  const currency = activeSettings.currency;

  const handleRateChange = (key: CostRateFieldKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Commit valid rates to context (persists to localStorage) on blur, so the heavy route list
  // doesn't re-render on every keystroke.
  const commitRates = () => {
    const parsed = parseCostSettings(form);
    if (parsed.isValid && parsed.settings) {
      const changed =
        parsed.settings.dailyFixedCostPerVehicle !== costSettings.dailyFixedCostPerVehicle ||
        parsed.settings.costPerKm !== costSettings.costPerKm ||
        parsed.settings.hourlyWage !== costSettings.hourlyWage ||
        parsed.settings.currency !== costSettings.currency;
      if (changed) {
        setCostSettings(parsed.settings);
        logEvent({ category: 'fixed_routes', action: 'fixed_route_cost_rates_changed' });
      }
    }
  };

  const handleCurrencyChange = (value: string) => {
    const next = { ...form, currency: value };
    setForm(next);
    const parsed = parseCostSettings(next);
    if (parsed.isValid && parsed.settings) {
      setCostSettings(parsed.settings);
      logEvent({ category: 'fixed_routes', action: 'fixed_route_cost_rates_changed', field: 'currency' });
    }
  };

  const handleOverrideChange = (route: FixedRoute, field: CostRateFieldKey, raw: string) => {
    const next: Partial<CostRates> = { ...(route.costOverride ?? {}) };
    const trimmed = raw.trim();
    if (trimmed === '') {
      delete next[field];
    } else {
      const parsed = parseNonNegativeNumber(trimmed);
      if (parsed === null) return; // ignore invalid keystroke
      next[field] = parsed;
    }
    const cleaned = Object.keys(next).length > 0 ? next : undefined;
    updateRoute({ ...route, costOverride: cleaned });
    logEvent({ category: 'fixed_routes', action: 'fixed_route_cost_override_set', field, cleared: trimmed === '' });
  };

  const handleResetOverride = (route: FixedRoute) => {
    updateRoute({ ...route, costOverride: undefined });
    logEvent({ category: 'fixed_routes', action: 'fixed_route_cost_override_set', cleared: true });
  };

  if (routes.length === 0) {
    return null;
  }

  return (
    <Box data-testid="fixed-routes.cost-section">
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Icon size={22} d={ICON_FALLBACKS.PaymentsOutlined} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('cost.sectionTitle')}
        </Typography>
      </Box>

      {/* Rate editor */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Icon size={18} d={ICON_FALLBACKS.Calculate} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('cost.editorTitle')}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('cost.editorHint')}
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }}
          gap={2}
        >
          <TextField
            select
            size="small"
            label={t('cost.currency')}
            value={form.currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon size={18} d={ICON_FALLBACKS.AttachMoney} />
                </InputAdornment>
              ),
            }}
          >
            {CURRENCY_OPTIONS.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
          {RATE_FIELDS.map((field) => (
            <TextField
              key={field.key}
              size="small"
              type="number"
              label={t(field.labelKey)}
              value={form[field.key]}
              onChange={(e) => handleRateChange(field.key, e.target.value)}
              onBlur={commitRates}
              error={Boolean(parsedForm.errors[field.key])}
              helperText={parsedForm.errors[field.key] ? t('cost.invalidRate') : undefined}
              inputProps={{ min: 0, step: 'any' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon size={18} d={field.iconPath} />
                  </InputAdornment>
                ),
              }}
            />
          ))}
        </Box>
      </Paper>

      {/* Network rollup */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }}
        gap={2}
        sx={{ mb: 2 }}
      >
        <RollupCard
          iconPath={ICON_FALLBACKS.AttachMoney}
          color="#2e7d32"
          label={t('cost.totalDaily')}
          value={formatCurrency(network.totalDailyCost, currency)}
          subtitle={t('cost.acrossRoutes', { count: routes.length })}
        />
        <RollupCard
          iconPath={ICON_FALLBACKS.Assessment}
          color="#1976d2"
          label={t('cost.totalAnnual')}
          value={formatCurrencyCompact(network.totalAnnualCost, currency)}
          subtitle={formatCurrency(network.totalAnnualCost, currency)}
        />
      </Box>

      {/* Breakdown */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {t('cost.breakdownTitle')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <BreakdownRow iconPath={ICON_FALLBACKS.DirectionsBusOutlined} label={t('cost.fixed')} value={formatCurrency(network.totalFixedCost, currency)} />
          <BreakdownRow iconPath={ICON_FALLBACKS.Straighten} label={t('cost.distance')} value={formatCurrency(network.totalDistanceCost, currency)} />
          <BreakdownRow iconPath={ICON_FALLBACKS.AccessTimeOutlined} label={t('cost.labour')} value={formatCurrency(network.totalLabourCost, currency)} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {t('cost.assumptionsNote')}
        </Typography>
      </Paper>

      {/* Per-route table */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
        {t('cost.perRouteTitle')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {sortedRoutes.map((rc) => {
          const route = routeById.get(rc.routeId);
          if (!route) return null;
          const isExpanded = expandedRouteId === rc.routeId;
          return (
            <Paper key={rc.routeId} sx={{ p: 1.5 }} elevation={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rc.routeName}
                  </Typography>
                  {rc.hasOverride && (
                    <Tooltip title={t('cost.overrideTooltip')}>
                      <Chip
                        icon={<Icon size={14} d={ICON_FALLBACKS.AutoFixHigh} />}
                        label={t('cost.override')}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', lineHeight: 1.2 }}>
                      {t('routeBuilder.list.costPerDay', { value: formatCurrency(rc.dailyCost, currency) })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('routeBuilder.list.costPerYear', { value: formatCurrencyCompact(rc.annualCost, currency) })}
                    </Typography>
                  </Box>
                  <Tooltip title={t('cost.editOverrideAria', { name: rc.routeName })}>
                    <IconButton
                      size="small"
                      aria-label={t('cost.editOverrideAria', { name: rc.routeName })}
                      onClick={() => setExpandedRouteId(isExpanded ? null : rc.routeId)}
                    >
                      <Icon size={18} name={isExpanded ? 'chevron-up' : 'chevron-down'} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Collapse in={isExpanded} unmountOnExit>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('cost.overrideHint')}
                </Typography>
                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', sm: 'repeat(3, 1fr)' }}
                  gap={1.5}
                >
                  {RATE_FIELDS.map((field) => {
                    const overrideVal = route.costOverride?.[field.key];
                    return (
                    <TextField
                      key={field.key}
                      size="small"
                      type="number"
                      label={t(field.labelKey)}
                      value={overrideVal != null ? String(overrideVal) : ''}
                      placeholder={String(costSettings[field.key])}
                      onChange={(e) => handleOverrideChange(route, field.key, e.target.value)}
                      inputProps={{ min: 0, step: 'any' }}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon size={16} d={field.iconPath} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    );
                  })}
                </Box>
                {rc.hasOverride && (
                  <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                      icon={<Icon size={14} d={ICON_FALLBACKS.Autorenew} />}
                      label={t('cost.resetOverride')}
                      size="small"
                      variant="outlined"
                      onClick={() => handleResetOverride(route)}
                      clickable
                    />
                  </Box>
                )}
              </Collapse>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

interface RollupCardProps {
  iconPath: string;
  color: string;
  label: string;
  value: string;
  subtitle?: string;
}

function RollupCard({ iconPath, color, label, value, subtitle }: RollupCardProps) {
  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ color, mr: 1.5, display: 'flex', alignItems: 'center' }}>
          <Icon size={20} d={iconPath} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color, mb: subtitle ? 0.5 : 0 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

interface BreakdownRowProps {
  iconPath: string;
  label: string;
  value: string;
}

function BreakdownRow({ iconPath, label, value }: BreakdownRowProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon size={18} d={iconPath} />
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}
